import React from 'react';
import { Link, Ta11yText } from '@core/index';
import { appAlert, appIconsPath, appLinkSize, notificationType, getKeyByValue } from '@utils/globalConstant';
import './Notification.scss';
import { tA11y } from '@utils/a11y/a11yHelpers';
import { useA11y } from '@context/Utils/A11y';
import Icons from '@core/Utils/Icons/Icons';

export function ApiError({
  title = 'ek_global_api_err_hdl1',
  message,
  isGenericApiError = true,
  handleAPIErrorLink = () => {}
}) {
  // const
  const textColor = 'text-danger';
  const titleContent = tA11y(title);
  const messageContent = isGenericApiError ? tA11y('ek_global_generic_api_err') : tA11y(message);

  return (
    <div className="p-12 border border-danger rounded">
      <Ta11yText textContent={titleContent} className="ek-labeltext-12-medium text-danger pb-8 m-0 text-start" tag="h2" />
      <div className="py-2 px-4" role="alert">
        <div className="d-flex flex-col align-items-center justify-content-start">
          <div className="pe-4 d-flex flex-col align-items-center justify-content-start">
            <Icons name='ERROR' colorType="dark" width={12} />
          </div>
          <Ta11yText textContent={messageContent} className={`ek-input-feedback-text ${textColor} py-0 m-0 text-start`} tag="div" />
        </div>
      </div>
      {!isGenericApiError && <Link
        label={'ek_global_defined_api_err_lnk'}
        linkStyle={`pt-8 w-100 text-danger`}
        linkSize={appLinkSize.SMALL}
        onClick={handleAPIErrorLink}
        iconRight={appIconsPath.ARROW_RIGHT_SMALL_RED}
      />}
    </div>
  );
}

export function NotificationSuggestion({
  type = notificationType.DEFAULT,
  title = 'Error Message',
  text = 'Text here',
  message = ['Message here', 'Message here']
}) {
  const textColor = 'text-primary';
  let titleColor = 'text-primary';
  let titleIcon = null;
  let icon = null;
  let borderColor = null;
  let bgColor = null;

  if (type === notificationType.VARIANT1) {
    titleIcon = appIconsPath.WARNING;
    icon = appIconsPath.ERROR;
    borderColor = 'border-ek-caution';
    bgColor = 'bg-ek-notification-error';
  } else if (type === notificationType.VARIANT2) {
    titleIcon = appIconsPath.CORRECT;
    titleColor = 'text-success';
    borderColor = 'border-ek-success';
    bgColor = 'bg-ek-notification-success';
  } else if (type === notificationType.VARIANT3) {
    bgColor = 'bg-ek-white';
  }

  return (
    <div className={`p-12 border rounded ${borderColor} ${bgColor}`}>
      <div className="d-flex justify-content-start gap-8">
        {titleIcon && <Icons name={getKeyByValue(appIconsPath,titleIcon)} className="pt-1" width={12} height={16} colorType='dark' />}

        <div className="p-0 m-0">
          <div className={`${titleColor} ek-labeltext-12-semibold m-0 text-start`}>{title}</div>
          <div className="mt-8" role="alert">
            <div className="d-flex flex-col align-items-center justify-content-start">
              {icon && (
                <div className="pe-4 d-flex flex-col align-items-center justify-content-start">
                  <Icons name={getKeyByValue(appIconsPath,icon)}  width={12} height={16} colorType='dark' />
                </div>
              )}

              <div className={`ek-input-feedback-text ${textColor} py-0 m-0 text-start`}>
                {text}
              </div>
            </div>
          </div>
          <div className="mt-8" role="alert">
            {message.map((msg, index) => (
              <div
                key={`${index}`}
                className="d-flex flex-col align-items-center justify-content-start">
                <div className={`ek-labeltext-12-semibold ${textColor} py-0 m-0 text-start`}>
                  {msg}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export function Notification({ type, message, position = 'start', isLiveRegion = true }) {
  const { tA11yTranslate, getStripeHTMLText } = useA11y();
  const icon =
    type === appAlert.ERROR || type === appAlert.DANGER
      ? appIconsPath.ERROR
      : type === appAlert.SUCCESS
      ? appIconsPath.CORRECT
      : false;

  const textColor =
    type === appAlert.ERROR || type === appAlert.DANGER
      ? 'text-danger'
      : type === appAlert.SUCCESS
      ? 'text-success'
      : 'text-info';

  return (
    <div className="pt-4 px-4 pb-2" {...(isLiveRegion ? { role: 'alert', 'aria-live': 'polite' } : {})}>
      <div
        className={`d-flex flex-col align-items-center 
        ${position === 'start' && 'justify-content-start'} 
        ${position === 'center' && 'justify-content-center'}`}>
        {icon ? (
          <div className="pe-4 d-flex flex-col align-items-center justify-content-start">
            <Icons 
              name={getKeyByValue(appIconsPath,icon)}
              width={12} 
              height={12} 
              colorType='dark'              
            />
          </div>
        ) : (
          <div className="icon-placeholder pe-4" />
        )}
        {tA11y(message).isHtml ? (
          <div
            aria-label={type === appAlert.ERROR || type === appAlert.DANGER ? `${icon ? tA11yTranslate('ek_global_pw_not_fulfilled')?.content : ''} ${getStripeHTMLText(tA11y(message).content)}` : `${icon ? tA11yTranslate('ek_global_pw_fulfilled')?.content : ''} ${getStripeHTMLText(tA11y(message).content)}`}
            className={`ek-input-feedback-text ${textColor} py-0 m-0 text-start`}
            dangerouslySetInnerHTML={{ __html: tA11y(message).content }}
          />
        ) : (
          <div 
            aria-label={type === appAlert.ERROR || type === appAlert.DANGER ? `${icon ? tA11yTranslate('ek_global_pw_not_fulfilled')?.content : ''} ${getStripeHTMLText(tA11y(message).content)}` : `${icon ? tA11yTranslate('ek_global_pw_fulfilled')?.content : ''} ${getStripeHTMLText(tA11y(message).content)}`}
            className={`ek-input-feedback-text ${textColor} py-0 m-0 text-start`}
          >
            {tA11y(message).content}
          </div>
        )}
      </div>
    </div>
  );
}

export default Notification;
